import type { ReactNode } from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../../lib/cn';
import type { ZIndex } from '../../tokens/z-index';

// ---- Root ----
export interface SelectProps extends SelectPrimitive.SelectProps {
  children: ReactNode;
}

export function Select({ children, ...rest }: SelectProps) {
  return <SelectPrimitive.Root {...rest}>{children}</SelectPrimitive.Root>;
}

// ---- Trigger ----
export interface SelectTriggerProps extends SelectPrimitive.SelectTriggerProps {
  placeholder?: string;
  size?: 'sm' | 'md';
}

const triggerSizeClasses: Record<NonNullable<SelectTriggerProps['size']>, string> = {
  sm: 'h-8 px-2 text-xs',
  md: 'h-9 px-3 text-sm',
};

export function SelectTrigger({
  children,
  placeholder,
  size = 'md',
  className,
  ...rest
}: SelectTriggerProps) {
  // `min-w-0` é o que faz o texto caber: num flex container, um filho só encolhe
  // abaixo da largura do seu conteúdo com isso — sem ele qualquer `truncate` que
  // a página aplique por dentro fica inerte e o rótulo longo vaza para fora do
  // campo, por cima do chevron.
  const valueClassName = 'min-w-0 flex-1 truncate text-left';
  const valueNode =
    children == null ? (
      <SelectPrimitive.Value className={valueClassName} placeholder={placeholder} />
    ) : (
      <SelectPrimitive.Value className={valueClassName} placeholder={placeholder}>
        {children}
      </SelectPrimitive.Value>
    );

  return (
    <SelectPrimitive.Trigger
      className={cn(
        'inline-flex items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white font-body text-secondary-950 transition-colors',
        'hover:border-slate-400',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[placeholder]:text-slate-600',
        triggerSizeClasses[size],
        className,
      )}
      {...rest}
    >
      {valueNode}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

// ---- Content ----
export interface SelectContentProps extends SelectPrimitive.SelectContentProps {
  /**
   * @deprecated O conteúdo do Select já abre no tier `Z.FLOATING` (500), acima
   * de qualquer modal (400). Não é mais necessário sinalizar "dentro de modal".
   * Mantido apenas para compatibilidade com call-sites existentes — é no-op.
   */
  inModal?: boolean;
  /**
   * Override do tier de z-index do conteúdo portal-rendered, aplicado via style
   * inline (vence as classes). Raramente necessário: o default `Z.FLOATING`
   * (500) já fica acima de modais. Use só para casos especiais de empilhamento.
   */
  zIndex?: ZIndex;
}

export function SelectContent({
  children,
  className,
  position = 'popper',

  inModal: _inModal = false,
  zIndex,
  style,
  ...rest
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        sideOffset={4}
        style={zIndex != null ? { ...style, zIndex } : style}
        className={cn(
          'min-w-[8rem] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-md',
          // Z.FLOATING (500) — portal-rendered floating UI sits above any modal
          // (400) by default, so a Select inside a Dialog/Sheet is never hidden
          // behind the backdrop. No per-call-site config needed.
          'z-[500]',
          'animate-in fade-in-0 zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          position === 'popper' && 'max-h-[var(--radix-select-content-available-height)]',
          className,
        )}
        {...rest}
      >
        <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1">
          <ChevronUp className="h-4 w-4 text-slate-500" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1">
          <ChevronDown className="h-4 w-4 text-slate-500" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

// ---- Item ----
export interface SelectItemProps extends SelectPrimitive.SelectItemProps {}

export function SelectItem({ children, className, ...rest }: SelectItemProps) {
  return (
    <SelectPrimitive.Item
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm font-body text-slate-700 outline-none',
        'focus:bg-slate-100 focus:text-secondary-950',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...rest}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4 text-primary-500" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

// ---- Separator ----
export function SelectSeparator({ className }: { className?: string }) {
  return <SelectPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-slate-200', className)} />;
}
